import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { MessageService } from 'primeng/api';
import { catchError, Observable, tap, throwError } from 'rxjs';

import { ServiceResponseV1 } from '@core/models/apis';
import { PaymentHistoryListQuery, PaymentHistorySummaryV1 } from '@core/models/payment-history';
import { environment } from '@src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class PaymentsService {
  private readonly http = inject(HttpClient);
  private readonly messageService = inject(MessageService);
  private readonly paymentsAdminUrl = `${environment.apiUrl}${environment.apis.paymentsEngineAdmin}/v1/payments`;

  private readonly payments$ = signal<PaymentHistorySummaryV1[]>([]);
  readonly payments = this.payments$.asReadonly();

  private readonly paging$ = signal<{ nextToken: string | null; totalSize: number }>({
    nextToken: null,
    totalSize: 0
  });
  readonly paging = this.paging$.asReadonly();

  getPaymentsList(query: PaymentHistoryListQuery = {}): Observable<ServiceResponseV1<PaymentHistorySummaryV1[]>> {
    console.log('[PaymentsService] getPaymentsList', query);
    let params = new HttpParams();
    if (query.youtopiaCustomerId) params = params.set('youtopiaCustomerId', query.youtopiaCustomerId);
    if (query.referenceId) params = params.set('referenceId', query.referenceId);
    if (query.pageSize !== undefined) params = params.set('pageSize', String(query.pageSize));
    if (query.nextToken) params = params.set('nextToken', query.nextToken);

    return this.http.get<ServiceResponseV1<PaymentHistorySummaryV1[]>>(this.paymentsAdminUrl, { params }).pipe(
      tap((response) => {
        this.payments$.set(response.data ?? []);
        this.paging$.set({
          nextToken: response.paging?.nextToken ?? null,
          totalSize: response.paging?.totalSize ?? 0
        });
      }),
      catchError((error) => this.errorHandler(error, 'Error getting payments list'))
    );
  }

  private errorHandler(error: unknown, serviceMessage: string): Observable<never> {
    const err = error as { error?: { message?: string }; message?: string };
    const message = error instanceof Error ? error.message : (err?.error?.message ?? err?.message ?? 'Unknown error');
    console.error(`[PaymentsService] ${serviceMessage}, ${message}`);
    this.messageService.add({ severity: 'error', summary: 'Error', detail: serviceMessage });
    return throwError(() => error);
  }
}
